import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { showingbookerAPI } from '../../../services/showingbookerAPI';

const Container = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
  background: #f8f9fa;
`;

const Title = styled.h3`
  margin: 0;
  color: #333;
  font-size: 1.3rem;
  font-weight: 600;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  font-size: 14px;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
  }
`;

const FilterInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #007AFF;
  }
`;

const ExportContainer = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const ExportButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #28a745;
  background: ${props => props.primary ? '#28a745' : 'white'};
  color: ${props => props.primary ? 'white' : '#28a745'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.primary ? '#1e7e34' : '#f0f8f0'};
  }
`;

const TableContainer = styled.div`
  max-height: 600px;
  overflow-y: auto;
  border-top: 1px solid #eee;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const TableHeaderCell = styled.th`
  padding: 15px 12px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #ddd;
  font-size: 14px;
  cursor: pointer;
  user-select: none;
  
  &:hover {
    background: #e9ecef;
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  &:hover {
    background: #f8f9fa;
  }
  
  &:nth-child(even) {
    background: #fafafa;
  }
  
  &:nth-child(even):hover {
    background: #f0f0f0;
  }
`;

const TableCell = styled.td`
  padding: 12px;
  border-bottom: 1px solid #eee;
  font-size: 14px;
  vertical-align: top;
`;

const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    switch(props.status) {
      case 'completed': return '#d4edda';
      case 'cancelled': return '#f8d7da';
      case 'no-show': return '#fff3cd';
      default: return '#e2e3e5';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'completed': return '#155724';
      case 'cancelled': return '#721c24';
      case 'no-show': return '#856404';
      default: return '#6c757d';
    }
  }};
`;

const ComplianceBadge = styled.span`
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  background: ${props => props.compliant ? '#d4edda' : '#f8d7da'};
  color: ${props => props.compliant ? '#155724' : '#721c24'};
  margin-left: 8px;
`;

const ViewButton = styled.button`
  padding: 4px 8px;
  border: 1px solid #6c757d;
  background: white;
  color: #6c757d;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #6c757d;
    color: white;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666;
`;

const CompletedShowings = ({ properties }) => {
  const [showings, setShowings] = useState([]);
  const [filteredShowings, setFilteredShowings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc'); // Most recent first
  const [filters, setFilters] = useState({
    property: '',
    status: '',
    resident: '',
    owner: '',
    dateFrom: '',
    dateTo: '',
    timeRange: '30' // Default to last 30 days
  });


  useEffect(() => {
    loadCompletedShowings();
  }, [properties]);

  const loadCompletedShowings = async () => {
    try {
      setLoading(true);
      const completedShowings = await showingbookerAPI.getCompletedShowings();
      
      // Transform API data to match component expectations
      const transformedShowings = completedShowings.map(showing => ({
        id: showing.id,
        propertyId: showing.property_id,
        propertyAddress: showing.property_address,
        datetime: `${showing.showing_date}T${showing.showing_time}`,
        date: showing.showing_date,
        time: showing.showing_time,
        buyerName: showing.buyer_name,
        buyerEmail: showing.buyer_email,
        buyerPhone: showing.buyer_phone,
        status: showing.status,
        notes: showing.notes,
        createdAt: showing.created_at
      }));
      
      setShowings(transformedShowings);
    } catch (error) {
      console.error('Error loading completed showings:', error);
      setShowings([]);
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  useEffect(() => {
    let filtered = [...showings];
    
    // Time range filter
    if (filters.timeRange) {
      const daysAgo = parseInt(filters.timeRange);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysAgo);
      filtered = filtered.filter(showing => new Date(showing.datetime) >= cutoffDate);
    }
    
    if (filters.property) {
      filtered = filtered.filter(showing => 
        showing.propertyAddress.toLowerCase().includes(filters.property.toLowerCase())
      );
    }
    
    if (filters.status) {
      filtered = filtered.filter(showing => showing.status === filters.status);
    }
    
    if (filters.resident) {
      filtered = filtered.filter(showing => 
        showing.residentName.toLowerCase().includes(filters.resident.toLowerCase())
      );
    }
    
    if (filters.owner) {
      filtered = filtered.filter(showing => 
        showing.ownerName.toLowerCase().includes(filters.owner.toLowerCase())
      );
    }
    
    if (filters.dateFrom) {
      filtered = filtered.filter(showing => showing.date >= filters.dateFrom);
    }
    
    if (filters.dateTo) {
      filtered = filtered.filter(showing => showing.date <= filters.dateTo);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (sortField === 'date') {
        aValue = new Date(a.datetime);
        bValue = new Date(b.datetime);
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    setFilteredShowings(filtered);
  }, [showings, filters, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc'); // Default to desc for dates
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const exportToCSV = () => {
    const headers = [
      'Date', 'Time', 'Property Address', 'Buyer Name', 'Buyer Contact', 'Agent', 'Status', 
      'Owner', 'Resident', 'Compliant', 'Advance Notice', 'Certified Mail', 'Duration (min)', 
      'Feedback', 'Payment Status'
    ];
    
    const csvData = [
      headers,
      ...filteredShowings.map(showing => [
        showing.date,
        showing.time,
        showing.propertyAddress,
        showing.buyerName,
        `${showing.buyerEmail} | ${showing.buyerPhone}`,
        showing.agentName || 'Direct Buyer',
        showing.status,
        showing.ownerName,
        showing.residentName,
        showing.isCompliant ? 'Yes' : 'No',
        showing.hasAdvanceNotice ? `${showing.advanceNoticeHours}h` : 'No',
        showing.hasCertifiedMail ? showing.certifiedMailDate : 'No',
        showing.duration || 'N/A',
        showing.feedback || 'None',
        showing.paymentStatus
      ])
    ];
    
    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `completed-showings-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const printCompliance = () => {
    const printWindow = window.open('', '_blank');
    const printContent = `
      <html>
        <head>
          <title>Completed Showings Compliance Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; border-bottom: 2px solid #28a745; padding-bottom: 10px; }
            .summary { background: #f8f9fa; padding: 15px; border-radius: 8px; margin: 20px 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { padding: 8px; border: 1px solid #ddd; text-align: left; font-size: 12px; }
            th { background: #f8f9fa; font-weight: 600; }
            .compliant { color: #155724; font-weight: 500; }
            .non-compliant { color: #721c24; font-weight: 500; }
            .status { padding: 2px 6px; border-radius: 4px; font-size: 11px; }
            .completed { background: #d4edda; color: #155724; }
            .cancelled { background: #f8d7da; color: #721c24; }
            .no-show { background: #fff3cd; color: #856404; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <h1>📋 Completed Showings Compliance Report</h1>
          <div class="summary">
            <p><strong>Report Generated:</strong> ${new Date().toLocaleDateString()}</p>
            <p><strong>Total Showings:</strong> ${filteredShowings.length}</p>
            <p><strong>Compliance Rate:</strong> ${Math.round((filteredShowings.filter(s => s.isCompliant).length / filteredShowings.length) * 100)}%</p>
            <p><strong>Completed Showings:</strong> ${filteredShowings.filter(s => s.status === 'completed').length}</p>
            <p><strong>Cancelled/No-Show:</strong> ${filteredShowings.filter(s => s.status !== 'completed').length}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Property</th>
                <th>Buyer</th>
                <th>Status</th>
                <th>Compliant</th>
                <th>Notice</th>
                <th>Certified Mail</th>
                <th>Payment</th>
              </tr>
            </thead>
            <tbody>
              ${filteredShowings.map(showing => `
                <tr>
                  <td>${showing.date}</td>
                  <td>${showing.propertyAddress}</td>
                  <td>${showing.buyerName}</td>
                  <td><span class="status ${showing.status}">${showing.status}</span></td>
                  <td class="${showing.isCompliant ? 'compliant' : 'non-compliant'}">${showing.isCompliant ? 'Yes' : 'No'}</td>
                  <td>${showing.hasAdvanceNotice ? showing.advanceNoticeHours + 'h' : 'No'}</td>
                  <td>${showing.hasCertifiedMail ? showing.certifiedMailDate : 'No'}</td>
                  <td>${showing.paymentStatus}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const viewDetails = (showing) => {
    alert(`Showing Details:\n\nProperty: ${showing.propertyAddress}\nBuyer: ${showing.buyerName}\nDate: ${showing.date} ${showing.time}\nStatus: ${showing.status}\nCompliance: ${showing.isCompliant ? 'Compliant' : 'Non-Compliant'}\n\nAdvance Notice: ${showing.hasAdvanceNotice ? showing.advanceNoticeHours + ' hours' : 'None'}\nCertified Mail: ${showing.hasCertifiedMail ? showing.certifiedMailDate : 'Not sent'}\n\n${showing.feedback ? 'Feedback: ' + showing.feedback : ''}`);
  };

  if (loading) {
    return (
      <Container>
        <LoadingState>
          <div>📋 Loading completed showings...</div>
        </LoadingState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <div>
          <Title>📋 Completed Showings</Title>
          <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
            Compliance & Records • {filteredShowings.length} showings • {Math.round((filteredShowings.filter(s => s.isCompliant).length / filteredShowings.length) * 100)}% compliant
          </div>
        </div>
        
        <ExportContainer>
          <ExportButton onClick={exportToCSV}>📄 CSV Export</ExportButton>
          <ExportButton onClick={printCompliance} primary>📋 Compliance Report</ExportButton>
        </ExportContainer>
      </Header>

      <div style={{ padding: '20px', borderBottom: '1px solid #eee', background: '#fafafa' }}>
        <FilterContainer>
          <FilterSelect
            value={filters.timeRange}
            onChange={(e) => handleFilterChange('timeRange', e.target.value)}
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
            <option value="">All time</option>
          </FilterSelect>
          
          <FilterSelect
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="no-show">No Show</option>
          </FilterSelect>
          
          <FilterInput
            type="text"
            placeholder="Filter by property..."
            value={filters.property}
            onChange={(e) => handleFilterChange('property', e.target.value)}
          />
          
          <FilterInput
            type="text"
            placeholder="Filter by owner..."
            value={filters.owner}
            onChange={(e) => handleFilterChange('owner', e.target.value)}
          />
          
          <FilterInput
            type="date"
            placeholder="From date"
            value={filters.dateFrom}
            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
          />
          
          <FilterInput
            type="date"
            placeholder="To date"
            value={filters.dateTo}
            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
          />
        </FilterContainer>
      </div>

      <TableContainer>
        {filteredShowings.length > 0 ? (
          <Table>
            <TableHeader>
              <tr>
                <TableHeaderCell onClick={() => handleSort('date')}>
                  Date & Time {sortField === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => handleSort('propertyAddress')}>
                  Property {sortField === 'propertyAddress' && (sortDirection === 'asc' ? '↑' : '↓')}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => handleSort('buyerName')}>
                  Buyer {sortField === 'buyerName' && (sortDirection === 'asc' ? '↑' : '↓')}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => handleSort('status')}>
                  Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                </TableHeaderCell>
                <TableHeaderCell>Compliance</TableHeaderCell>
                <TableHeaderCell>Owner/Resident</TableHeaderCell>
                <TableHeaderCell>Details</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {filteredShowings.map(showing => (
                <TableRow key={showing.id}>
                  <TableCell>
                    <div style={{ fontWeight: '500' }}>{showing.date}</div>
                    <div style={{ fontSize: '13px', color: '#666' }}>{showing.time}</div>
                    {showing.duration && (
                      <div style={{ fontSize: '12px', color: '#666' }}>{showing.duration} min</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div style={{ fontWeight: '500' }}>{showing.propertyAddress}</div>
                    {showing.feedback && (
                      <div style={{ fontSize: '12px', color: '#007AFF', marginTop: '2px' }}>
                        💬 {showing.feedback}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div style={{ fontWeight: '500' }}>{showing.buyerName}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{showing.buyerEmail}</div>
                    {showing.agentName && (
                      <div style={{ fontSize: '12px', color: '#666' }}>via {showing.agentName}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={showing.status}>
                      {showing.status.charAt(0).toUpperCase() + showing.status.slice(1)}
                    </StatusBadge>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      ${showing.bookingFee} • {showing.paymentStatus}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <span style={{ color: showing.isCompliant ? '#155724' : '#721c24', fontWeight: '500' }}>
                        {showing.isCompliant ? '✅ Compliant' : '⚠️ Issues'}
                      </span>
                    </div>
                    {showing.isOccupied && (
                      <div style={{ fontSize: '12px', marginTop: '4px' }}>
                        <div style={{ color: showing.hasAdvanceNotice ? '#155724' : '#721c24' }}>
                          📧 Notice: {showing.hasAdvanceNotice ? `${showing.advanceNoticeHours}h` : 'Missing'}
                        </div>
                        <div style={{ color: showing.hasCertifiedMail ? '#155724' : '#721c24' }}>
                          📮 Mail: {showing.hasCertifiedMail ? showing.certifiedMailDate : 'Not sent'}
                        </div>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div style={{ fontSize: '13px' }}>
                      <div><strong>Owner:</strong> {showing.ownerName}</div>
                      <div><strong>Resident:</strong> {showing.residentName}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <ViewButton onClick={() => viewDetails(showing)}>
                      👁️ View
                    </ViewButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <EmptyState>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
            <h3>No completed showings</h3>
            <p>Completed showings and compliance records will appear here.</p>
          </EmptyState>
        )}
      </TableContainer>
    </Container>
  );
};

export default CompletedShowings;